import { useMemo, useState } from "react";
import { Accordion } from "../../core/basic/Accordion";
import Card from "../../core/basic/Card";
import { Flex } from "../../../../styled-system/jsx";
import { Button } from "../../core/basic/Button";
import { css } from "../../../../styled-system/css";
import { TextField } from "../../fields/base/TextField";
import { apiClient } from "../../../zodios/client";
import { useQuery } from "@tanstack/react-query";
import { type Organization } from "../../../zodios/common/orgSchema";
import { createColumnBuilder } from "../../table/columnBuilder";
import { OrgCell } from "../../../modules/organization/OrgCell";
import { useClientTable } from "../../table/tableFeatures";
import { Table } from "../../table/Table";
import { toTitleCase } from "../../../utils/formatText";
import { queryClient } from "../../../config/react-query";
import { useTranslation } from "react-i18next";

const columnHelper = createColumnBuilder<Organization>();

const INITIAL_SEARCH_FORM_DATA = {
  amazonProfileName: "",
  asin: "",
  sellerID: "",
};

const OrgFinder = () => {
  const { t } = useTranslation(["admin", "common"]);
  const [searchFormData, setSearchFormData] = useState(INITIAL_SEARCH_FORM_DATA);

  const orgSearchQuery = useQuery({
    queryKey: ["orgSearch"],
    queryFn: async () =>
      await apiClient.searchOrganizations({
        queries: {
          profile_name: searchFormData.amazonProfileName,
          asin: searchFormData.asin,
          seller_id: searchFormData.sellerID,
        },
      }),
    enabled: false,
  });

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        header: t(($) => $.admin["Organization"]),
        enableHiding: false,
        enableSorting: true,
        cell: (props) => {
          return <OrgCell orgData={props.row.original} changeOrgOnClick={false} />;
        },
        size: 350,
      }),
      columnHelper.accessor("id", {
        header: t(($) => $.admin["Organization ID"]),
      }),
      columnHelper.accessor("profile_type", {
        header: t(($) => $.admin["Profile Type"]),
      }),
      columnHelper.accessor("org_type", {
        header: t(($) => $.admin["Channel"]),
        cell: ({ getValue }) => {
          return toTitleCase(getValue());
        },
      }),
      columnHelper.accessor("status", {
        header: t(($) => $.admin["Status"]),
        cell: ({ getValue }) => {
          return toTitleCase(getValue());
        },
      }),
      columnHelper.accessor("marketplaces", {
        header: t(($) => $.admin["Marketplaces"]),
        cell: ({ getValue }) => {
          return getValue().join(", ");
        },
      }),
      columnHelper.accessor("account_manager", {
        header: t(($) => $.admin["Account Manager ID"]),
      }),
    ];
  }, [t]);

  const table = useClientTable({
    data: orgSearchQuery.data?.orgs,
    columns,
    enableGlobalFilter: true,
  });

  return (
    <Accordion title={t(($) => $.admin["Find Organizations"])}>
      <Card css={{ padding: "xlarge" }}>
        <p className={css({ fontSize: "medium" })}>
          {t(($) => $.admin["To search for a connected organization fill in at least one field below."])}
        </p>
        <form className={css({ display: "flex", gap: "medium", flexDirection: "column", width: "500px" })}>
          <TextField
            label={t(($) => $.admin["Amazon Profile Name"])}
            value={searchFormData.amazonProfileName}
            onChange={(value) => setSearchFormData({ ...searchFormData, amazonProfileName: value })}
          />
          <TextField
            label={t(($) => $.admin["ASIN"])}
            value={searchFormData.asin}
            onChange={(value) => setSearchFormData({ ...searchFormData, asin: value })}
          />
          <TextField
            label={t(($) => $.admin["Seller ID"])}
            value={searchFormData.sellerID}
            onChange={(value) => setSearchFormData({ ...searchFormData, sellerID: value })}
          />
          <Flex gap={"xlarge"} justifyContent={"center"}>
            <Button
              variant="secondary"
              onPress={() => {
                setSearchFormData(INITIAL_SEARCH_FORM_DATA);

                void queryClient.resetQueries({ queryKey: ["orgSearch"] });
              }}
            >
              {t(($) => $.common.actions["Clear"])}
            </Button>
            <Button onPress={async () => await orgSearchQuery.refetch()} isPending={orgSearchQuery.isFetching}>
              {t(($) => $.common.actions["Search"])}
            </Button>
          </Flex>
        </form>
        {orgSearchQuery.isSuccess && !orgSearchQuery.data.orgs.length && (
          <p
            className={css({
              width: "500px",
              marginTop: "large",
              fontSize: "medium",
              textAlign: "center",
            })}
          >
            {t(($) => $.admin["No matching organizations where found."])}
          </p>
        )}
        {orgSearchQuery.data && orgSearchQuery.data.orgs.length ?
          <Table table={table} />
        : null}
      </Card>
    </Accordion>
  );
};

export default OrgFinder;
